/* eslint-disable camelcase */
import { useEffect, useRef, useState } from 'react';
import styles from './styles';
import { Box, Grid, IconButton, TextField, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { AddRounded, DeleteRounded, RemoveRounded, SaveRounded } from '@mui/icons-material';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { useDispatch } from 'react-redux';
import { isMobile } from 'react-device-detect';
import { parametriTablecColumns } from './table_config';
import { HYDRAULICS_RUN_DETAILS, MAX_ROW_LIMITS, PARAMETER_ANALYSIS_TYPE, PARAMETER_ANALYSIS_TYPE_VALUE } from '../../../../utils/constants';
import { enqueueSnackbar } from '../../../../store/actions/snackbar';
import SelectEditValueTypes from '../../../ui/SelectEditValueTypes';
import { COLORS } from '../../../../shared/config/colors';
import Datagrid from '../../../ui/Datagrid';
import DeleteConfirmationModal from '../../../deleteConfirmationModal';
import MobileStrippingRunInput from '../../../mobile/hydraulics/featuredWellRunInputs/strippingRunInput';
import MobileSelectEditValueTypes from '../../../mobile/hydraulics/SelectEditValueTypes';

function generateRandom() {
  const length = 24;
  const charset = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let retVal = '';
  for (let i = 0, n = charset.length; i < length; ++i) {
    retVal += charset.charAt(Math.floor(Math.random() * n));
  }
  return retVal;
}

const RenderValueCell = ({ row, updateData, setIsDirtyForId, params }) => {
  const [values, setValues] = useState(row?.values?.length ? row?.values : ['', '']);
  const inputRefs = useRef([]);
  const isFirstRow = params?.api?.getRowIndexRelativeToVisibleRows(params?.id) === 0;
  const maxLength = isFirstRow ? 7 : 4;

  useEffect(() => {
    setValues(row?.values?.length ? row.values : ['', '']);
  }, [row.values]);

  const handleChange = (index, newValue) => {
    const updatedValues = [...values];
    updatedValues[index] = newValue === '' ? '' : Number(newValue);
    setValues(updatedValues);
    updateData(row._id, 'values', updatedValues);
  };

  const addInputField = () => {
    if (values.length < maxLength) {
      setValues([...values, '']);
    }
  };

  const removeInputField = () => {
    if (values.length > 2) {
      const updatedValues = values.slice(0, -1);
      setValues(updatedValues);
      updateData(row._id, 'values', updatedValues);
    }
  };

  const handleKeyDown = (event, index) => {
    if (event.key === 'Tab') {
      event.preventDefault();
      const direction = event.shiftKey ? -1 : 1;
      const nextIndex = index + direction;

      if (nextIndex >= 0 && nextIndex < values.length) {
        const nextInput = inputRefs.current[nextIndex];
        if (nextInput) {
          nextInput.focus();
          nextInput.select();
        }
      }
    }
  };

  return (
    <Grid
      container
      spacing={{ xs: 0, sm: 1 }}
      alignItems="center"
      justifyContent={{ xs: 'flex-start', md: 'center' }}
      wrap="wrap"
    >
      {values.map((val, index) => (
        <Grid
          item
          key={index}
          xs={12} sm="auto"
        >
          <TextField
            type="number"
            value={val}
            onChange={(e) => handleChange(index, e.target.value)}
            size="small"
            inputRef={(el) => (inputRefs.current[index] = el)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            inputProps={{
              inputMode: 'decimal',
              pattern: '[0-9]*[.,]?[0-9]*',
              style: {
                appearance: 'textfield',
                textAlign: 'center'
              },
            }}
            sx={{
              width: { xs: '100%', sm: '100px' },
              '& input[type=number]': {
                MozAppearance: 'textfield',
              },
              '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                WebkitAppearance: 'none',
                margin: 0,
              },
            }}
          />
        </Grid>
      ))}

      <Grid item xs={6} sm="auto" display="flex" justifyContent="center">
        <IconButton
          onClick={() => {
            setIsDirtyForId(row?._id);
            addInputField();
          }}
          disabled={values.length >= maxLength}
        >
          <AddRounded />
        </IconButton>
      </Grid>

      {/* - button */}
      <Grid item xs={6} sm="auto" display="flex" justifyContent="center">
        <IconButton
          onClick={() => {
            setIsDirtyForId(row?._id);
            removeInputField();
          }}
          disabled={values.length <= 2}
        >
          <RemoveRounded />
        </IconButton>
      </Grid>
    </Grid>
  );
};

RenderValueCell.propTypes = {
  row: PropTypes.any,
  updateData: PropTypes.func,
  setIsDirtyForId: PropTypes.func,
  params: PropTypes.object
};

const ParametricAnalysisRunInputs = ({
  hydraulicsParameter,
  proxyLoader,
  setHydraulicsParameterData,
  parametricAnalysisData,
  setParametricAnalysisData,
  saveHydraulicDetails,
  setParameterDirty
}) => {
  const classes = styles();
  const apiRef = useGridApiRef();
  const dispatch = useDispatch();
  const [properties, setProperties] = useState([]);
  const [invalidRows, setInvalidRows] = useState({});
  const [deleteAction, setDeleteAction] = useState(false);
  const [deleteRow, setDeleteRow] = useState();
  const units = hydraulicsParameter?.units?.parametricAnalysis;

  useEffect(() => {
    if (parametricAnalysisData) {
      const resultArray = (parametricAnalysisData || []).map((item) => {
        const matchedKey = Object.keys(PARAMETER_ANALYSIS_TYPE_VALUE)
          .find(key => PARAMETER_ANALYSIS_TYPE_VALUE[key] === item.parameter) || item.parameter;

        const decimalPlaces = units.inputs?.[matchedKey]?.decimals ?? 2;
        return {
          ...item,
          parameter: PARAMETER_ANALYSIS_TYPE_VALUE[matchedKey] || matchedKey,
          values: item.values?.map(val => {
            if (val === '' || val === null || val === undefined) return val;
            const numericValue = Number(val);
            if (isNaN(numericValue)) return val;

            return parseFloat(numericValue.toFixed(decimalPlaces));
          }) || [],
          _id: item._id,
        };
      });
      setProperties(resultArray);
      setHydraulicsParameterData(resultArray);
    }
    return () => {
      setHydraulicsParameterData([]);
    };
  }, [parametricAnalysisData]);

  useEffect(() => {
    const lastIndex = properties.length - 1;
    const lastRow = properties[lastIndex];
    if (lastRow?.parameter === '') {
      setTimeout(() => {
        apiRef.current.scrollToIndexes({
          rowIndex: lastIndex
        });
      }, 50);
    }
  }, [properties?.length]);

  const addNewParameterRow = () => {
    const newData = [...properties];
    const nextId = properties.length;
    const newRow = {
      parameter: '',
      values: ['', ''],
      grid_id: nextId + 1,
      _id: generateRandom(),
      isNewRow: true,
      isDirty: true,
    };
    newData.push(newRow);
    setProperties(newData);
  };

  const handleAddParameter = () => {
    if (properties[0]?.isNewRow) return;
    dispatch(addNewParameterRow());
  };

  const updateData = (id, field, value) => {
    setProperties((prevProperties) => {
      const existingParameter = prevProperties.find(
        (item) => item.parameter === value && item._id !== id
      );
      if (field === 'parameter' && existingParameter) {
        dispatch(
          enqueueSnackbar(
            `${existingParameter.parameter} already selected.`,
            'warning',
            new Date().getTime() + Math.random()
          )
        );
        return prevProperties;
      }

      const updatedData = prevProperties.map((item) => {
        if (item._id === id) {
          return {
            ...item,
            [field]: value,
            isDirty: JSON.stringify(value) !== JSON.stringify(item[field]),
          };
        }
        return item;
      });
      setHydraulicsParameterData(updatedData);
      return updatedData;
    });
  };

  const setIsDirtyForId = (targetId) => {
    setProperties(prevData =>
      prevData.map(item =>
        item._id === targetId ? { ...item, isDirty: true } : item
      )
    );
  };

  const getFilteredParameterTypes = (properties = [], currentValue) => {
    const existingParameters = properties.map(item => item.parameter);
    return PARAMETER_ANALYSIS_TYPE.filter(param => !existingParameters.includes(param) || param === currentValue);
  };

  const renderParameterDropDown = (params, properties = []) => {
    const currentValue = params?.value ?? '';
    const filteredData = getFilteredParameterTypes(properties, currentValue);

    if (isMobile) {
      return (
        <MobileSelectEditValueTypes
          {...params}
          enableSort={false}
          closeCellAfterSelect={true}
          updateData={updateData}
          data={filteredData}
          value={currentValue}
        />
      );
    }

    return (
      <SelectEditValueTypes
        {...params}
        enableSort={false}
        closeCellAfterSelect={true}
        updateData={updateData}
        data={filteredData}
        value={currentValue}
      />
    );
  };

  const showError = (message, rowId) => {
    dispatch(enqueueSnackbar(message, 'error'));
    setInvalidRows(prev => ({ ...prev, [rowId]: true }));
    return false;
  };

  const validateParameterDetails = (details) => {
    const { _id, values, parameter } = details;
    const min_rop = units.inputs.rop.min;
    const max_rop = units.inputs.rop.max;
    const min_holeDepth = units.inputs.hole_depth.min;
    const min_flow_in = units.inputs.flow_in.min;
    const min_rpm = units.inputs.rpm.min;
    const max_rpm = units.inputs.rpm.max;
    const min_mw = units.inputs.mw.min;
    const max_mw = units.inputs.mw.max;

    if (new Set(values).size !== values.length) {
      return showError('Please check Value. Should not have duplicate values', _id);
    }

    if (parameter === 'Hole Depth' && values.some(value => value < min_holeDepth)) {
      return showError(HYDRAULICS_RUN_DETAILS.HOLE_DEPTH, _id);
    }
    if (parameter === 'Flow Rate' && values.some(value => value < min_flow_in)) {
      return showError(HYDRAULICS_RUN_DETAILS.FLOW_IN, _id);
    }
    if (parameter === 'RPM' && values.some(value => value < min_rpm || value > max_rpm)) {
      return showError(`RPM should be between ${min_rpm} and ${max_rpm}`, _id);
    }
    if (parameter === 'ROP' && values.some(value => value < min_rop || value > max_rop)) {
      return showError(`ROP should be between ${min_rop} and ${max_rop}`, _id);
    }
    if (parameter === 'MW' && values.some(value => value < min_mw || value > max_mw)) {
      return showError(`Mud Weight should be between ${min_mw} and ${max_mw}`, _id);
    }
    return true;
  };

  const checkHydraulicsParameterValues = (value) => {
    const row = value;
    const invalidFields = [];

    if (row.parameter === '' && !row?.values?.every(val => typeof val === 'number' && !isNaN(val))) {
      invalidFields.push('Please select a parameter and Enter values');
    }

    if (row.parameter === '' && row?.values?.every(val => typeof val === 'number' && !isNaN(val))) {
      invalidFields.push('Please select a parameter');
    }

    if (row.parameter !== '' && !row?.values?.every(val => typeof val === 'number' && !isNaN(val))) {
      invalidFields.push(`Please enter valid values for: ${row?.parameter}`);
    }

    if (invalidFields.length === 0) {
      setInvalidRows(prev => ({ ...prev, [row._id]: false }));
      return validateParameterDetails(row);
    } else {
      setInvalidRows(prev => ({ ...prev, [row._id]: true }));
      dispatch(
        enqueueSnackbar(
          `${invalidFields.join(', ')}`,
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return false;
    }
  };

  const formatParametricAnalysisData = (updatedProperties, row) => {
    return updatedProperties.map((item) => {
      const key =
        Object.keys(PARAMETER_ANALYSIS_TYPE_VALUE).find(
          (k) => PARAMETER_ANALYSIS_TYPE_VALUE[k] === item?.parameter
        ) || row?.parameter;

      if (key === 'mw') {
        saveHydraulicDetails();
      }

      return {
        parameter: key,
        values: item?.values,
        _id: item?._id,
      };
    });
  };

  const saveRowData = (row) => {
    if (checkHydraulicsParameterValues(row)) {
      setProperties((prev) => {
        const updated = prev.map((item) =>
          item._id === row._id
            ? { ...item, isNewRow: false, isDirty: false }
            : item
        );
        setParametricAnalysisData(formatParametricAnalysisData(updated, row));
        setParameterDirty(prev => {
          if (!Array.isArray(prev) || prev.length === 0) return prev;
          const updated = [...prev];
          updated[0] = { ...updated[0], isDirty: true };
          return updated;
        });
        return updated;
      });
    }
  };

  const removeRow = (row) => {
    setProperties((prev) => {
      const updated = prev.filter((item) => item._id !== row._id);

      setParametricAnalysisData(formatParametricAnalysisData(updated, row));
      setParameterDirty(prev => {
        if (!Array.isArray(prev) || prev.length === 0) return prev;
        const updated = [...prev];
        updated[0] = { ...updated[0], isDirty: true };
        return updated;
      });
      return updated;
    });

    setDeleteAction(false);
    setDeleteRow({});
  };

  const renderActionCell = ({
    row
  }) => {
    return (
      <Grid container justifyContent="center">
        <IconButton
          variant="solid"
          disabled={!row.isDirty || hydraulicsParameter.loading}
          onClick={() => {
            saveRowData(row);
          }}
        >
          <SaveRounded />
        </IconButton>
        <IconButton
          variant="solid"
          disabled={hydraulicsParameter.loading}
          onClick={() => {
            setDeleteAction(true);
            setDeleteRow(row);
          }}
        >
          <DeleteRounded />
        </IconButton>
      </Grid>
    );
  };

  if (apiRef.current === null) apiRef.current = {};

  return (
    <>
      <Grid container>
        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
          <Box display="flex" alignItems="center" marginLeft={'13px'}>
            <Typography variant='button' sx={{ cursor: 'pointer' }} fontWeight='700' onClick={handleAddParameter} className={hydraulicsParameter.loading || properties[0]?.isNewRow || properties?.length === MAX_ROW_LIMITS.PARAMETRIC || properties?.some(item => item.isDirty === true) ? classes.disableIcon : ''}>+ Add</Typography>
            <Typography variant='h6' sx={{ marginLeft: '12px', fontSize: isMobile && '16px' }} color={COLORS.LIGHT_GREY} fontWeight='500'>{` (Note: Maximum ${MAX_ROW_LIMITS.PARAMETRIC} entries are allowed)`}</Typography>
          </Box>
        </Box>
        <Grid item xs={12}>
          {isMobile
            ? (
              <MobileStrippingRunInput
                data={properties}
                columns={parametriTablecColumns(renderParameterDropDown, RenderValueCell, renderActionCell, updateData, setIsDirtyForId)}
                updateData={updateData}
                loading={proxyLoader}
              />
              )
            : (
              <Datagrid
                apiRef={apiRef}
                data={properties}
                sx={{
                  marginLeft: '-6px',
                  '.MuiDataGrid-cell': {
                    fontSize: '1rem',
                    fontWeight: 900,
                    textAlign: 'justify',
                    pointerEvents: proxyLoader ? 'none' : 'auto',
                    backgroundColor: proxyLoader ? '#f5f5f5' : 'inherit'
                  },
                  '.MuiDataGrid-columnHeader': {
                    fontSize: '1.2rem !important',
                    fontWeight: '900 !important'
                  },
                }}
                height={'auto'}
                loading={hydraulicsParameter.loading}
                columns={parametriTablecColumns(renderParameterDropDown, RenderValueCell, renderActionCell, updateData, setIsDirtyForId)}
                onRowClick={(params, evt) => {
                  evt.preventDefault();
                  evt.stopPropagation();
                }}
                disableStickyHeader={false}
                hideFooter={true}
                getRowId={(row) => row._id}
                isRowSelectable={() => !proxyLoader}
                getRowClassName={(params) =>
                  proxyLoader ? 'Mui-disabled' : (invalidRows[params.id] ? classes.invalidRow : '')
                }
                pageSize={100}
                editMode="cell"
                toolbar={{
                  options: {
                    columns: false,
                    filters: false,
                    density: false,
                    export: false
                  },
                }}
                enableDirtyLock
              />
              )}
        </Grid>
      </Grid>
      {
        deleteAction && <DeleteConfirmationModal
          open={deleteAction}
          handleDelete={removeRow}
          handleClose={setDeleteAction}
          deleteRow={deleteRow}
        />
      }
    </>
  );
};
ParametricAnalysisRunInputs.propTypes = {
  hydraulicsParameter: PropTypes.object,
  featuredWellRun: PropTypes.object,
  setHydraulicsParameterData: PropTypes.func,
  proxyLoader: PropTypes.bool,
  parametricAnalysisData: PropTypes.array,
  setParametricAnalysisData: PropTypes.func,
  setParameterDirty: PropTypes.func,
  saveHydraulicDetails: PropTypes.func,
};

export default ParametricAnalysisRunInputs;
